/** Photo URLs and client-side downscaling. Remote mode streams from /img; local demo mode keeps blobs in IndexedDB. */
import type { Row } from '../shared/types';
import { esc } from '../shared/util';
import { S } from './state';

export const seedSVG = (p: { seed_color?: string; seed_label?: string }) => 'data:image/svg+xml,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" fill="${p.seed_color}"/><rect x="30" y="24" width="60" height="72" rx="6" fill="none" stroke="rgba(255,255,255,.5)" stroke-width="4"/><text x="60" y="68" font-family="Helvetica,Arial,sans-serif" font-size="24" font-weight="700" fill="rgba(255,255,255,.88)" text-anchor="middle">${esc(p.seed_label || '')}</text></svg>`);

export const Img = {
  db: null as IDBDatabase | null,
  urls: new Map<string, string>(),
  async open() {
    try {
      this.db = await new Promise<IDBDatabase>((res, rej) => { const r = indexedDB.open('moveinv-photos', 1); r.onupgradeneeded = () => r.result.createObjectStore('blobs'); r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); r.onblocked = () => rej(new Error('blocked')); });
    } catch { this.db = null; }
  },
  async put(key: string, blob: Blob) {
    this.urls.set(key, URL.createObjectURL(blob));
    if (!this.db) return;
    await new Promise<void>(res => { try { const tx = this.db!.transaction('blobs', 'readwrite'); tx.objectStore('blobs').put(blob, key); tx.oncomplete = () => res(); tx.onerror = () => res(); } catch { res(); } });
  },
  async load(key?: string | null) {
    if (!key || this.urls.has(key) || !this.db) return;
    const blob = await new Promise<Blob | null>(res => { try { const r = this.db!.transaction('blobs').objectStore('blobs').get(key); r.onsuccess = () => res(r.result); r.onerror = () => res(null); } catch { res(null); } });
    if (blob) this.urls.set(key, URL.createObjectURL(blob));
  },
  async del(key?: string | null) { if (!this.db || !key) return; try { this.db.transaction('blobs', 'readwrite').objectStore('blobs').delete(key); } catch {} },
  async warm(photos: Row[]) { await Promise.all(photos.filter(p => !p.seed_color).flatMap(p => [p.r2_key, p.thumb_r2_key]).map(k => Promise.race([this.load(k), new Promise(r => setTimeout(r, 1500))]))); },
  /** URL for a photo row (or a public-payload photo {id,url,seed_color}). */
  src(p: Row | undefined | null, full = false): string {
    if (!p) return seedSVG({ seed_color: '#C9CED6', seed_label: '' });
    if (p.seed_color) return seedSVG(p);
    if (p.url) return full ? p.url : p.url + '?t=1';
    if (S.mode === 'remote') return `/img/${p.id}${full ? '' : '?t=1'}`;
    return this.urls.get(full ? p.r2_key : (p.thumb_r2_key || p.r2_key)) || seedSVG({ seed_color: '#9AA3AF', seed_label: '…' });
  },
};

export function downscale(file: File | Blob, max: number, q = 0.82): Promise<{ blob: Blob; w: number; h: number }> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => {
      const s = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement('canvas'); c.width = Math.round(img.width * s); c.height = Math.round(img.height * s);
      c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
      c.toBlob(b => { URL.revokeObjectURL(img.src); b ? res({ blob: b, w: c.width, h: c.height }) : rej(new Error('Could not encode image')); }, 'image/jpeg', q);
    };
    img.onerror = () => rej(new Error('Could not read that image'));
    img.src = URL.createObjectURL(file);
  });
}
